import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import ProgressBar from "../../../../../../Common/LinearProgress";
import { setPrevState } from "../../../../../../../util/common";
import { ButtonUI, ConfirmPopup } from "../../../../../../Common";
import Workspace from "../../../../../../../service/workspace";
import { showToast } from "../../../../../../../store/actions";
import RunJobAsComponent from "../../../../utils/RunJobAsComponent";
import { handleEditJob, saveJobDetails } from "../../../../../../../store/actions/datapipeline";
import { DATA_SOURCE } from "../../../../../../../util/Data/DataModeling";

const DATA_MODELING_JOBS = [1, 4, 5, 6];
const NON_MODELING_JOBS = [2, 3, 7, 8, 9];

const ConflictPopup = ({ data, JOB_TYPE, loading, handleJobRun, handleCancel }) => (
  <ConfirmPopup
    configData={{
      popupTitle: (
        <Box sx={{ m: 0, p: 0, display: "flex", alignItems: "center" }}>
          <ReportProblemIcon color="warning" sx={{ mr: "8px" }} />
          <Typography>Attention</Typography>
        </Box>
      ),
      popupMessage: (
        <>
          <Typography variant="body2">
            {JOB_TYPE === 1 &&
              `One or more running jobs have same ${DATA_SOURCE.plural.toLowerCase()} selected and/or mapped entities.`}
            {JOB_TYPE === 4 && `One or more running jobs have same ${DATA_SOURCE.plural.toLowerCase()} selected.`}
            {[5, 6].includes(JOB_TYPE) &&
              `One or more running jobs have same data entities selected and/or has a mapped ${DATA_SOURCE.singular.toLowerCase()} in another job.`}
          </Typography>
          {data.jobs?.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                Conflicting Jobs
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.jobs.map((job, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={job}>
                      <ListItemText primary={`${job}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          {data.dataSources.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                {`Conflicting ${DATA_SOURCE.plural}`}
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.dataSources.map((ds, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={ds}>
                      <ListItemText primary={`${ds}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          {data.entities.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                Conflicting Entities
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.entities.map((entity, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={entity}>
                      <ListItemText primary={`${entity}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          <Typography variant="body2" mt={2}>
            Running this job concurrently may lead to inconsistent output or potential data loss.
          </Typography>
        </>
      ),
      handleCancel,
      handleClick: () => handleJobRun(false),
      confirmBtnName: "Run Anyway",
      loadingForConfirm: loading,
    }}
  />
);

function RunTimePanel({
  state,
  setState,
  options = [],
  extSnowflakeClusterList = [],
  clusterError,
  participantsExcludeVisitor,
  restrictOrNot,
  handleSave,
  transformJobState,
  isSnowflake = false,
  isExternalSnowflake = false,
}) {
  const { Workspace_id } = useParams();
  const dispatch = useDispatch();

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const workspace = useSelector((state) => state.workspace);
  const reducerUser = useSelector((state) => state.user);

  const user = useMemo(() => reducerUser.currentUser, [reducerUser]);
  const {
    id: Job_id,
    data: { type: JobType },
  } = useMemo(() => dataPipeline.editJob, [dataPipeline]);

  const JOB_TYPE = useMemo(() => Number(JobType), [JobType]);

  const isNew = Job_id?.startsWith("dndnode");
  const job = useMemo(() => dataPipeline.job, [dataPipeline]);

  const { accessRole } = useMemo(() => workspace?.workspace, [workspace]);

  const { runJobAsUserId, runJobAsUserFullName } = useMemo(() => state, [state]);
  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);

  const locationType = useMemo(
    () => transformJobState?.locationList.find(({ value }) => value === transformJobState.location)?.type || "",
    [transformJobState.location, transformJobState?.locationList]
  );

  const connectionClusters = useMemo(() => {
    if (locationType === "snowflake") {
      if (transformJobState.location === "SNOWFLAKE") return options;
      return extSnowflakeClusterList;
    } else if (isExternalSnowflake) return extSnowflakeClusterList;
    return options;
  }, [extSnowflakeClusterList, isExternalSnowflake, locationType, options, transformJobState.location]);

  const showRunJobAs = useMemo(
    () =>
      (JOB_TYPE === 2 && isSnowflake && !isExternalSnowflake) ||
      ([3, 8].includes(JOB_TYPE) && transformJobState.location === "SNOWFLAKE"),
    [JOB_TYPE, isSnowflake, isExternalSnowflake, transformJobState.location]
  );

  const showClusters = useMemo(
    () =>
      !(
        (JOB_TYPE === 2 && !isExternalSnowflake) ||
        [7, 9].includes(JOB_TYPE) ||
        ([3, 8].includes(JOB_TYPE) && locationType !== "snowflake")
      ),
    [JOB_TYPE, isExternalSnowflake, locationType]
  );

  const showStopJob = useMemo(() => job.status === "running" || job.status === "terminating", [job.status]);

  const [loading, setLoading] = useState(false);
  const [runNowDisable, setRunNowDisable] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openStopConfirm, setOpenStopConfirm] = useState(false);
  const [conflictConfirm, setConflictConfirm] = useState({
    open: false,
    data: {},
  });
  const [openRunConfirm, setOpenRunConfirm] = useState(false);

  const handleStateChange = useCallback((key, value) => setPrevState(setState, { [key]: value }), [setState]);

  const handleUserChange = useCallback(
    (e, { runJobAsUserId, runJobAsUserFullName }) => {
      setPrevState(setState, { runJobAsUserId, runJobAsUserFullName });
    },
    [setState]
  );

  const handleConflict = (data) =>
    setConflictConfirm({
      open: true,
      data,
    });

  const handleJobRun = useCallback(
    async (validate = true, panelClosed = false) => {
      if (DATA_MODELING_JOBS.includes(JOB_TYPE)) setLoading(true);
      else {
        setRunNowDisable(true);
      }

      if ((JOB_TYPE === 3 || Number(JOB_TYPE) === 8) && transformJobState.location !== "SNOWFLAKE")
        setRunNowDisable(true);

      const wsService = new Workspace();

      let response = null;
      if (JOB_TYPE === 2) response = await wsService.runDataExchangeJob(Workspace_id, Job_id);
      else if (JOB_TYPE === 3) response = await wsService.runDataTranformJob(Workspace_id, Job_id);
      else if (JOB_TYPE === 7) response = await wsService.runNotebookJob(Workspace_id, Job_id);
      else if (JOB_TYPE === 8) response = await wsService.runRoutingJob(Workspace_id, Job_id);
      else if (JOB_TYPE === 9) response = await wsService.runNotificationJob(Workspace_id, Job_id);
      else response = await wsService.dataloadJob("init", Workspace_id, Job_id, JOB_TYPE, validate);

      if (response.success) {
        if (response.data?.conflict) handleConflict(response.data);
        else {
          const { status, StartTime, averageRunTimeInMS } = response.data;

          if (!panelClosed)
            dispatch(handleEditJob({ ...dataPipeline.editJob, data: { ...dataPipeline.editJob.data, status } }));

          dispatch(
            saveJobDetails({
              status,
              StartTime,
              averageRunTimeInMS,
            })
          );

          dispatch(
            showToast({
              message: response.message,
              type: "success",
            })
          );
        }
      }

      setRunNowDisable(false);
      if (DATA_MODELING_JOBS.includes(JOB_TYPE)) {
        if (validate) setOpenConfirm(false);
        else setConflictConfirm({ open: false });
        setLoading(false);
      }
    },
    [JOB_TYPE, Job_id, Workspace_id, dataPipeline.editJob, dispatch, transformJobState.location]
  );

  const handleRunConfirm = useCallback(async () => {
    const { errorInJob = false, closePanel = false } = await handleSave(false);
    if (!errorInJob) handleJobRun(true, closePanel);
  }, [handleJobRun, handleSave]);

  const handleStopJobExecution = useCallback(async () => {
    if (DATA_MODELING_JOBS.includes(JOB_TYPE)) setLoading(true);
    else setRunNowDisable(true);

    const wsService = new Workspace();
    let response = await wsService.stopJobExecutionById(Workspace_id, Job_id, JOB_TYPE);

    if (response.success) {
      const { status, StartTime, averageRunTimeInMS } = response.data;
      dispatch(handleEditJob({ ...dataPipeline.editJob, data: { ...dataPipeline.editJob.data, status } }));
      dispatch(
        saveJobDetails({
          status,
          StartTime,
          averageRunTimeInMS,
        })
      );
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
    }

    setRunNowDisable(false);
    if (DATA_MODELING_JOBS.includes(JOB_TYPE)) setLoading(false);
    setOpenStopConfirm(false);
  }, [JOB_TYPE, Job_id, Workspace_id, dataPipeline.editJob, dispatch]);

  useEffect(() => {
    let interval = null;
    const fetchData = async () => {
      if (isNew || !(job.status === "running" || job.status === "terminating")) {
        if (interval) clearInterval(interval);
        return;
      }

      const wsService = new Workspace();
      const { data, success } = await wsService.getAllJobs(Workspace_id, {
        search: "",
        JOB_TYPEList: [job.Type],
        jobIds: [Job_id],
      });

      if (success) {
        const { status, StartTime, averageRunTimeInMS } = data.jobs[0];
        dispatch(
          saveJobDetails({
            status,
            StartTime,
            averageRunTimeInMS,
          })
        );
      }
    };

    fetchData();
    interval = setInterval(fetchData, 20000); // Update every 20 seconds

    return () => clearInterval(interval);
  }, [Job_id, Workspace_id, dispatch, isNew, job.Type, job.status]);

  return (
    <>
      <Grid container direction={"row"} spacing={2}>
        {showClusters && (
          <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
            <FormControl required fullWidth variant="outlined">
              <InputLabel>Compute Cluster</InputLabel>
              <Select
                value={state.cluster}
                onChange={(e) => handleStateChange("cluster", e.target.value)}
                label="Compute Cluster"
                MenuProps={{ sx: { height: "220px" } }}
                error={clusterError}
                errorText={"Cluster does not exists or you do not have access to work on it."}
              >
                {(connectionClusters || [])?.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        {showRunJobAs && (
          <Grid sx={{ width: { xs: "100%", sm: "300px" } }}>
            <RunJobAsComponent
              participants={participantsExcludeVisitor}
              selectedUser={runJobAsUserId}
              handleChange={handleUserChange}
              handleRunUnderMyName={(e) =>
                handleUserChange(e, { runJobAsUserId: user.User_id, runJobAsUserFullName: currentUserFullName })
              }
              error={Job_id && !state.runJobAsUserId}
              editable={accessRole === 1 && participantsExcludeVisitor.length > 0}
              nonEditableValue={!state.runJobAsUserId ? "" : runJobAsUserFullName}
              fullWidth
            />
          </Grid>
        )}
        <Grid size={12}>
          {showStopJob ? (
            <ButtonUI
              onClick={(e) => {
                e.stopPropagation();
                setOpenStopConfirm(true);
              }}
              variant="contained"
              color="error"
              endIcon={<StopCircleOutlinedIcon />}
              disabled={isNew || runNowDisable || job.status === "terminating"}
            >
              Stop Job
            </ButtonUI>
          ) : (
            <ButtonUI
              onClick={(e) => {
                if (restrictOrNot()) {
                  setOpenRunConfirm(true);
                  return;
                }

                let allowedToRun = !["running", "draft"].includes(job.status);

                if (DATA_MODELING_JOBS.includes(job.Type) && allowedToRun) setOpenConfirm(true);
                else if (NON_MODELING_JOBS.includes(job.Type) && allowedToRun) handleJobRun();
              }}
              variant="contained"
              color="success"
              endIcon={<PlayCircleOutlineIcon />}
              disabled={isNew || runNowDisable || ["running", "draft"].includes(job.status)}
            >
              Run Job
            </ButtonUI>
          )}
        </Grid>
        {job.status === "running" && job.averageRunTimeInMS !== null && (
          <Grid size={6}>
            <ProgressBar StartTime={job.StartTime} averageRunTimeInMS={job.averageRunTimeInMS} />
          </Grid>
        )}
      </Grid>
      {openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle:
              JOB_TYPE === 1
                ? "Load Data to Data Warehouse"
                : JOB_TYPE === 4
                ? "Run Data Quality Job"
                : JOB_TYPE === 5
                ? "Run Data Mastering Job"
                : "Run Data Deduplication Job",
            popupMessage: "Are you sure you want to continue?",
            handleCancel: () => setOpenConfirm(false),
            handleClick: () => handleJobRun(true),
            loadingForConfirm: loading,
          }}
        />
      )}
      {openRunConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Save and Run the Job?",
            popupMessage: "You have unsaved changes. Do you want to save and run the job?",
            handleCancel: () => setOpenRunConfirm(false),
            handleClick: handleRunConfirm,
            loadingForConfirm: loading,
          }}
        />
      )}
      {conflictConfirm.open && (
        <ConflictPopup
          handleJobRun={() => handleJobRun(false)}
          loading={loading}
          handleCancel={() => setConflictConfirm({ open: false })}
          data={conflictConfirm.data}
          JOB_TYPE={JOB_TYPE}
        />
      )}
      {openStopConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: `Terminate "${job.Name || ""}" Job`,
            popupMessage: "Are you sure you want to terminate this job?",
            handleCancel: () => setOpenStopConfirm(false),
            handleClick: () => handleStopJobExecution(),
            loadingForConfirm: loading,
          }}
        />
      )}
    </>
  );
}

RunTimePanel.propTypes = {
  options: PropTypes.array.isRequired,
  participantsExcludeVisitor: PropTypes.array.isRequired,
  state: PropTypes.object.isRequired,
  setState: PropTypes.func.isRequired,
  restrictOrNot: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  transformJobState: PropTypes.object.isRequired,
  isSnowflake: PropTypes.bool,
  isExternalSnowflake: PropTypes.bool,
};

export default React.memo(RunTimePanel);
